/** Context provider for managing the currently selected items in the template scene sidebar. */
import { dateUtils, notEmpty, type Optional } from '@festina-life/core'
import { useDebounceCallback } from '@festina-life/ui/hooks'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useParams } from 'react-router'

import type { SpecialCasedSubTemplatedValueSetup } from '../components/specialCasedSubTemplatedViewers/types'
import { buildReferenceHierarchy } from '../components/subTemplateHierarchy/plottingHelpers'
import { useIsSpecialCasedSubTemplatedFieldValue } from '../components/templateFieldsTable/useIsSpecialCasedSubTemplatedFieldValue'
import { useLayoutContext } from '../context/layoutContext'
import { getInOutPathFromFullPathOnTemplateVersion } from '../context/pathHelpers'
import { useNavigationParams } from '../system/useNavigationParams'
import type { ComparableField, DiffSearchMode } from '../types/diffing'
import {
  areVersionsEqual,
  isSubTemplatedFieldValue,
  type TemplateVersion,
} from '../types/templateModel'
import { useTemplateReferenceNode } from '../uiModels/templateReference/useTemplateReferenceNode'
import { useTemplateVersion } from '../uiModels/useTemplateVersion'
import { useTemplateHierarchy } from '../uiModels/versionInfo/useTemplateHierarchy'
import { useTemplateVersionInfo } from '../uiModels/versionInfo/useTemplateVersionInfo'
import { useConfigurationContext } from './configurationContext'
import { getInheritedValue, getPathValue } from './diffHelpers'
import { InheritedTemplateVersionRegistrator } from './InheritedTemplateVersionRegistrator'
import { ReferencedTemplateVersionRegistrator } from './ReferencedTemplateVersionRegistrator'
import { Provider } from './templateContext'
import { useLazyLayerMetaModel } from './useLazyLayerMetaModel'
import { useSelectedPreviousVersion } from './useSelectedPreviousVersion'

/**
 * React Router params for the template scene.
 */
export type TemplateSceneParams = {
  templateId: string
}

type Props = {
  children: ReactNode
}

/** Context provider for controlling the template configuration. */
const TemplateProvider = ({ children }: Props) => {
  // region: Routing dependent params
  const { templateId } = useParams<TemplateSceneParams>()
  const { hash: currentHash } = useLocation()
  // endregion: Routing dependent params

  const {
    preferences: {
      templates: { isFlattened, isShowingEmptyValues, isShowingNativeView },
    },
  } = useLayoutContext()

  // region: Local state
  // Currently active versions hosted as state by the TemplateProvider for diffing purposes.
  const [activeVersions, setActiveVersions] = useState<
    Record<string, TemplateVersion>
  >({})
  const [referencedVersions, setReferencedVersions] = useState<
    Record<string, TemplateVersion>
  >({})
  const [collapsedTemplatePaths, setCollapsedTemplatePaths] = useState<
    Record<string, string[]>
  >({})
  const [templateScrollPaths, setTemplateScrollPaths] = useState<
    Record<string, string>
  >({})
  // endregion: Local state

  // region: External context dependencies
  const { viewAt, viewAtString } = useConfigurationContext()
  // endregion: External context dependencies

  // region: Template visual model hooks

  const { layer, block: viewingBlock } = useNavigationParams()
  const metaModel = useLazyLayerMetaModel(layer?.layerId)
  const versionInfo = useTemplateVersionInfo({
    id: templateId,
    date: viewAt,
  })

  const previousTemplateOperationKey = useRef(versionInfo?.operationKey)

  const templateVersion = useTemplateVersion({
    versionInfo,
    isFlattened,
  })
  const {
    previousVersion,
    setSelectedPreviousVersionForDiffingOperationKey,
    isDiffingAgainstShadowedVersion,
  } = useSelectedPreviousVersion(templateVersion)
  const unflattenedTemplateVersion = useTemplateVersion({
    versionInfo,
    isFlattened: false,
  })
  const detailsPath = useMemo(() => {
    const matchedPath = decodeURI(currentHash.substring(1))

    const acceptedPath =
      currentHash && templateVersion
        ? getInOutPathFromFullPathOnTemplateVersion({
            initialPath: matchedPath,
            templateVersion,
            metaModel,
            isShowingEmptyValues,
          })
        : undefined

    return acceptedPath === matchedPath ? acceptedPath : ''
  }, [currentHash, metaModel, templateVersion, isShowingEmptyValues])

  const hierarchy = useTemplateHierarchy({
    forTemplate: versionInfo,
    withReplacements: true,
  })

  const isSpecialCasedValue = useIsSpecialCasedSubTemplatedFieldValue()

  const templateReferenceNode = useTemplateReferenceNode({
    typeName: templateVersion?.modelType?.typeName,
    at: dateUtils.dateAsServerStr(viewAt),
    id: templateId!,
  })

  const referencedTemplateIds = useMemo(
    () =>
      templateReferenceNode
        ? [
            ...new Set(
              buildReferenceHierarchy(templateReferenceNode)
                .flatMap((hierarchy) => hierarchy.descendants())
                .map((treeNode) => treeNode.data.id),
            ),
          ].sort()
        : [],
    [templateReferenceNode],
  )

  // endregion: Template visual model hooks

  useLayoutEffect(
    function resetLocalStateOnTemplateChange() {
      if (previousTemplateOperationKey.current !== versionInfo?.operationKey) {
        setActiveVersions({})
        setReferencedVersions({})

        previousTemplateOperationKey.current = versionInfo?.operationKey
      }
    },
    [templateId, versionInfo?.operationKey],
  )

  useLayoutEffect(
    function resetActiveVersionsOnDateChange() {
      setActiveVersions({})
      setReferencedVersions({})
    },
    [viewAtString],
  )

  useEffect(
    function resetScrollPositionsOnViewChanges() {
      setTemplateScrollPaths({})
    },
    [isFlattened, isShowingEmptyValues],
  )

  const handleRegisterActiveVersion = useCallback(
    (activeVersion: TemplateVersion) => {
      setActiveVersions((currentVersions) => ({
        ...currentVersions,
        [activeVersion.versionInfo.identifier]: activeVersion,
      }))
    },
    [],
  )

  const handleRegisterReferencedVersion = useCallback(
    (activeVersion: TemplateVersion) => {
      setReferencedVersions((currentVersions) => ({
        ...currentVersions,
        [activeVersion.versionInfo.identifier]: activeVersion,
      }))
    },
    [],
  )

  const fieldValueForPath = useCallback(
    <T extends ComparableField>(
      path: string,
      searchMode: DiffSearchMode,
    ): Optional<T> => {
      if (unflattenedTemplateVersion && searchMode === 'unflattened') {
        return getPathValue<T>({
          path,
          version: unflattenedTemplateVersion,
          shouldSkipEmpty: true,
          metaModel,
        })
      }

      if (templateVersion) {
        if (searchMode === 'previous' || searchMode === 'current') {
          const fromVersion =
            searchMode === 'previous' ? previousVersion : templateVersion

          if (fromVersion) {
            return getPathValue<T>({
              path,
              version: fromVersion,
              isEnforcingSameTemplate: searchMode === 'previous',
              shouldSkipEmpty: false,
              metaModel,
            })
          }
        } else if (searchMode === 'inherited') {
          return getInheritedValue<T>({
            path,
            hierarchy: hierarchy
              .slice(0, -1)
              .map((versionInfo) => activeVersions[versionInfo.identifier])
              .filter(notEmpty),
            metaModel,
          })
        }
      }

      return null
    },
    [
      activeVersions,
      hierarchy,
      metaModel,
      previousVersion,
      templateVersion,
      unflattenedTemplateVersion,
    ],
  )

  const selectedDetails = useMemo(() => {
    let detail = detailsPath ? fieldValueForPath(detailsPath, 'current') : null

    if (
      detail &&
      isSubTemplatedFieldValue(detail) &&
      !isShowingNativeView &&
      isSpecialCasedValue(detail)
    ) {
      detail = {
        field: detail.subTemplatedField,
        subTemplateIdentifier: detail.subTemplateIdentifier,
      } as SpecialCasedSubTemplatedValueSetup
    }

    return detail
  }, [detailsPath, fieldValueForPath, isShowingNativeView, isSpecialCasedValue])

  // Versions from different blocks are not considered drafts for the current block, even if they are drafts in their own block.
  const isDraftTemplate =
    !!templateVersion?.versionInfo?.isDraft &&
    templateVersion.versionInfo.block === viewingBlock
  const isMappingsTemplate =
    !!templateVersion?.modelType?.templateType?.stereoTypes.includes(
      'MappingTemplate',
    )

  const handleChangePreviousTemplateVersion = useCallback(
    (toOperationKey: Optional<string>) => {
      setSelectedPreviousVersionForDiffingOperationKey(
        toOperationKey ?? undefined,
      )
    },
    [setSelectedPreviousVersionForDiffingOperationKey],
  )

  const handleToggleCollapsedPaths = useCallback(
    (path: string) => {
      if (templateId) {
        const collapsedPathsForTemplate =
          collapsedTemplatePaths[templateId] ?? []

        setCollapsedTemplatePaths({
          ...collapsedTemplatePaths,
          [templateId]: collapsedPathsForTemplate.includes(path)
            ? collapsedPathsForTemplate.filter(
                (collapsedPath) => collapsedPath !== path,
              )
            : [...collapsedPathsForTemplate, path],
        })
      }
    },
    [collapsedTemplatePaths, templateId],
  )

  const handleCheckPathCollapsed = useCallback(
    (path: string) =>
      (!!templateId && collapsedTemplatePaths[templateId]?.includes(path)) ??
      false,
    [collapsedTemplatePaths, templateId],
  )

  const handleUpdateScrollPath = useDebounceCallback(
    (scrollPath: string) => {
      if (templateId) {
        setTemplateScrollPaths({
          ...templateScrollPaths,
          [templateId]: scrollPath,
        })
      }
    },
    200,
    [templateId, templateScrollPaths],
  )

  const handleGetScrollPath = useCallback(
    () => (templateId ? templateScrollPaths[templateId] : undefined),
    [templateId, templateScrollPaths],
  )

  return (
    <Provider
      value={{
        detailsPath,
        viewingDetails: selectedDetails,
        isDraftTemplate,
        templateVersion,
        fieldValueForPath,
        previousTemplateVersion: previousVersion,
        onChangePreviousTemplateVersionOperationKey:
          handleChangePreviousTemplateVersion,
        isDiffingAgainstShadowedVersion,
        referencedVersions,
        isMappingsTemplate,
        onToggleTemplatePath: handleToggleCollapsedPaths,
        isPathCollapsed: handleCheckPathCollapsed,
        onUpdateScrollPath: handleUpdateScrollPath,
        getScrollPath: handleGetScrollPath,
      }}
    >
      {hierarchy
        .filter(
          (versionInfo) =>
            templateVersion &&
            !areVersionsEqual(versionInfo, templateVersion?.versionInfo),
        )
        .map((versionInfo) => (
          <InheritedTemplateVersionRegistrator
            key={`${templateVersion?.versionInfo.identifier}-${templateVersion?.versionInfo.fromDate}-${templateVersion?.versionInfo.isDraft}-${versionInfo.identifier}`}
            versionInfo={versionInfo}
            onRegisterActiveVersion={handleRegisterActiveVersion}
          />
        ))}
      {referencedTemplateIds.map((templateId) => (
        <ReferencedTemplateVersionRegistrator
          key={templateId}
          templateId={templateId}
          onRegisterVersion={handleRegisterReferencedVersion}
        />
      ))}
      {children}
    </Provider>
  )
}

export { TemplateProvider }
